/**
 * useControlTowerIA — orquestador único del "Centro de Control Logístico IA".
 *
 * Compone, SIN inventar datos, lo que ya existe + 2 piezas nuevas de backend:
 *   · useTorreControlData  → KPIs operativos, conteos por estado, pendientes,
 *                            incidencias (operational-summary + status-count…).
 *   · useActividadData     → rutas/repartidores del día + GPS en vivo (socket).
 *   · dashboardService.getAtRisk → Paquetes en riesgo (cerca del cutoff 21:00).
 *   · getForecasts → Proyección de paquetes del día (forecast_daily_snapshots).
 *   · getPackagesByStatusCount → conteos por estado (tiles: pendiente=status0…).
 *   · getActiveDeliveryUsers → repartidores activos (roster) → KPI.
 *   · deliveryQualityService.getAiPerformanceMetrics → señal de salud IA (semáforo).
 *
 * Todo scoped al logisticCompanyId del usuario de display logueado. Las piezas
 * nuevas degradan a "no disponible" si el backend aún no responde (no tumban la
 * pantalla ni vuelven el semáforo rojo por error de red).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import useAuthScope from '../../hooks/useAuthScope';
import { getOperationTimezone } from '../../utils/timezone';
import { dashboardService } from '../../services/dashboardService';
import deliveryQualityService from '../../services/deliveryQualityService';
import { getActiveDeliveryUsers } from '../../api/user';
import { getForecasts } from '../../api/forecasting';
import { getPackagesByStatusCount } from '../../api/packages';
import { deliveredFromResume } from '../../utils/packagesResume';
import useTorreControlData from '../adminView/torreControl/useTorreControlData';
import useActividadData from '../adminView/actividad/useActividadData';

const AUTO_REFRESH_MS = 90000;

const LEVEL_ORDER = { success: 0, warning: 1, error: 2 };

/** Salud del pipeline IA reducida a un nivel (regla mínima, defensiva). */
function deriveAiHealthLevel(metrics, expectedWorkers = 1) {
  if (!metrics) return null;
  const queue = metrics.queue_now || {};
  const tp = metrics.throughput || {};
  const wait = metrics.queue_wait_seconds || {};
  const alive = Number(queue.workers_alive || 0);
  const waitP95 = Number(wait.total?.p95 || 0);
  const total = Number(tp.total || 0);
  const failRate = total > 0 ? Number(tp.failed || 0) / total : 0;

  if (alive < expectedWorkers || waitP95 > 300 || failRate > 0.25) return 'error';
  if (waitP95 > 60 || failRate > 0.15) return 'warning';
  return 'success';
}

/**
 * Semáforo "Estado General" — combinado, peor regla manda. Devuelve el nivel
 * global + la lista de señales que lo componen (para que el panel explique POR
 * QUÉ está en ese color y no contradiga los paneles individuales).
 */
function deriveStatus({ kpis, aiHealthLevel, atRisk }) {
  const signals = [];

  const ns = kpis?.serviceLevelPct ?? 0;
  signals.push({
    key: 'ns',
    label: 'Nivel de servicio',
    value: `${Math.round(ns)}%`,
    level: ns >= 90 ? 'success' : ns >= 75 ? 'warning' : 'error',
  });

  const total = kpis?.total || 0;
  if (total > 0) {
    const failRate = ((kpis?.failed || 0) / total) * 100;
    signals.push({
      key: 'fail',
      label: 'Tasa de fallos',
      value: `${failRate.toFixed(1)}%`,
      level: failRate < 3 ? 'success' : failRate <= 7 ? 'warning' : 'error',
    });
  }

  if (aiHealthLevel) {
    signals.push({
      key: 'ai',
      label: 'Salud IA',
      value:
        aiHealthLevel === 'success'
          ? 'OK'
          : aiHealthLevel === 'warning'
            ? 'Atención'
            : 'Crítica',
      level: aiHealthLevel,
    });
  }

  if (atRisk?.available) {
    const count = atRisk.count || 0;
    const critical = atRisk.critical || 0;
    signals.push({
      key: 'risk',
      label: 'Paquetes en riesgo',
      value: String(count),
      level: critical > 0 ? 'error' : count > 0 ? 'warning' : 'success',
    });
  }

  const level = signals.reduce(
    (worst, s) => (LEVEL_ORDER[s.level] > LEVEL_ORDER[worst] ? s.level : worst),
    'success',
  );

  return { level, signals };
}

export default function useControlTowerIA({ date }) {
  const { user } = useAuth();
  const { logisticCompanyId: scopeLcid } = useAuthScope();
  const tz = useMemo(() => getOperationTimezone(user), [user]);

  // ── Núcleo reutilizado ────────────────────────────────────────────────────
  const torre = useTorreControlData({ date });
  const actividad = useActividadData({ date });

  const logisticCompanyId = torre.logisticCompanyId || scopeLcid || null;

  // ── Extras (riesgo + roster + proyección + conteos por estado) ──────────────
  const [atRisk, setAtRisk] = useState({ loading: true, available: false, count: 0, critical: 0, items: [] });
  const [activeDriversCount, setActiveDriversCount] = useState(null);
  const [forecast, setForecast] = useState({ loading: true, available: false, projected: null, lo: null, hi: null });
  const [funnelCounts, setFunnelCounts] = useState(null);
  const [aiHealthLevel, setAiHealthLevel] = useState(null);
  const firstLoad = useRef(true);

  const fetchExtras = useCallback(async () => {
    if (!logisticCompanyId) return;

    const settle = (promise, onOk, onFail) => promise.then(onOk).catch(onFail);

    await Promise.all([
      // Paquetes en riesgo (endpoint nuevo; degrada si no existe).
      settle(
        dashboardService.getAtRisk(logisticCompanyId, date, tz),
        (r) => {
          const d = r?.data ?? {};
          setAtRisk({
            loading: false,
            available: true,
            count: d.count ?? (Array.isArray(d.items) ? d.items.length : 0),
            critical: d.critical ?? 0,
            items: Array.isArray(d.items) ? d.items : [],
          });
        },
        () => setAtRisk({ loading: false, available: false, count: 0, critical: 0, items: [] }),
      ),

      // Repartidores activos (roster) de la empresa → KPI "Repartidores activos".
      settle(
        getActiveDeliveryUsers({ logisticCompanyId }),
        (res) => {
          const raw = res?.data;
          const arr = Array.isArray(raw) ? raw : raw?.docs || raw?.users || raw?.data || [];
          setActiveDriversCount(Array.isArray(arr) ? arr.length : null);
        },
        () => setActiveDriversCount(null),
      ),

      // Proyección de paquetes del día (forecast_daily_snapshots; scope por JWT).
      settle(
        getForecasts({ metric: 'created', granularity: 'daily' }),
        (series) => {
          const points = (Array.isArray(series) && series[0]?.points) || [];
          const todayPoint = points.find((p) => p.targetDate === date) || null;
          setForecast({
            loading: false,
            available: !!todayPoint && todayPoint.yhat != null,
            projected: todayPoint?.yhat ?? null,
            lo: todayPoint?.lo80 ?? null,
            hi: todayPoint?.hi80 ?? null,
          });
        },
        () => setForecast({ loading: false, available: false, projected: null, lo: null, hi: null }),
      ),

      // Embudo operacional — conteo crudo por estado (incluye status0 = pendiente).
      settle(
        getPackagesByStatusCount({ logisticCompanyId, startDate: date, endDate: date }),
        (c) =>
          setFunnelCounts({
            pendiente: (c?.status0 || 0) + (c?.status100 || 0), // status 0 (creado) + etiqueta impresa
            recolectado: (c?.status1 || 0) + (c?.status2 || 0), // recolectado + en bodega
            ruta: c?.status3_30 || 0,
            entregado: deliveredFromResume(c),
            fallido: c?.status6_7 || 0,
          }),
        () => setFunnelCounts(null),
      ),

      // Salud IA para el semáforo (opcional; si falla, no aporta señal).
      settle(
        deliveryQualityService.getAiPerformanceMetrics({
          window: '24h',
          logistic_company_id: logisticCompanyId,
        }),
        (r) => setAiHealthLevel(deriveAiHealthLevel(r?.data ?? null)),
        () => setAiHealthLevel(null),
      ),
    ]);

    if (firstLoad.current) firstLoad.current = false;
  }, [logisticCompanyId, date, tz]);

  useEffect(() => {
    firstLoad.current = true;
    setAtRisk({ loading: true, available: false, count: 0, critical: 0, items: [] });
    setForecast({ loading: true, available: false, projected: null, lo: null, hi: null });
  }, [date]);

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    fetchExtras();
    const id = setInterval(() => {
      if (!document.hidden) fetchExtras();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [logisticCompanyId, fetchExtras]);

  // ── Derivados ───────────────────────────────────────────────────────────────
  const gpsLiveDrivers = useMemo(
    () => (actividad.rutas || []).filter((r) => r.device?.gps).length,
    [actividad.rutas],
  );
  const totalDriversToday = (actividad.rutas || []).length;

  const status = useMemo(
    () => deriveStatus({ kpis: torre.kpis, aiHealthLevel, atRisk }),
    [torre.kpis, aiHealthLevel, atRisk],
  );

  const refetchAll = useCallback(() => {
    torre.refetch();
    actividad.refetch();
    fetchExtras();
  }, [torre, actividad, fetchExtras]);

  return {
    // contexto
    logisticCompanyId,
    tz,
    // núcleo operativo
    op: torre.op,
    kpis: torre.kpis,
    counts: torre.counts,
    pendingBreakdown: torre.pendingBreakdown,
    incidents: torre.incidents,
    rutas: actividad.rutas,
    gpsConnected: actividad.gpsConnected,
    gpsLiveDrivers,
    totalDriversToday,
    activeDriversCount,
    // riesgo + proyección + conteos por estado
    atRisk,
    forecast,
    funnelCounts,
    // semáforo
    status,
    // estado de carga
    loading: torre.loading,
    refreshing: torre.refreshing,
    error: torre.error,
    lastUpdated: torre.lastUpdated,
    refetch: refetchAll,
  };
}
